export type ListingPhotoIssueDefinition = {
  id: string;
  label: string;
  description: string;
  messageText: string;
};

/** Preset slugs tied to listing photo quality (Phase 5 admin ↔ seller loop). */
export const LISTING_PHOTO_FEEDBACK_SLUGS = [
  "image_quality",
  "image_quality_main",
  "image_quality_photo_2",
  "image_quality_photo_3",
] as const;

export type ListingPhotoFeedbackSlug =
  (typeof LISTING_PHOTO_FEEDBACK_SLUGS)[number];

export type ListingPhotoSlotIndex = 1 | 2 | 3;

/** Slot index → preset slug (D15). */
export const LISTING_PHOTO_SLOT_SLUG: Record<
  ListingPhotoSlotIndex,
  Exclude<ListingPhotoFeedbackSlug, "image_quality">
> = {
  1: "image_quality_main",
  2: "image_quality_photo_2",
  3: "image_quality_photo_3",
};

export const ALL_LISTING_PHOTOS_SLUG: ListingPhotoFeedbackSlug =
  "image_quality";

export function isListingPhotoFeedbackSlug(
  slug: string,
): slug is ListingPhotoFeedbackSlug {
  return (LISTING_PHOTO_FEEDBACK_SLUGS as readonly string[]).includes(slug);
}

export type ListingImageForSlotOrder = {
  url: string;
  position?: number | null;
  is_main?: boolean | null;
};

export type OrderedListingPhotoSlot = {
  slot: ListingPhotoSlotIndex;
  url: string;
};

/**
 * Order listing images main-first then position — matches review-round snapshot slots.
 */
export function orderItemImagesForReviewSlots(
  images: ListingImageForSlotOrder[],
): OrderedListingPhotoSlot[] {
  const sorted = [...images].sort((a, b) => {
    if (a.is_main && !b.is_main) return -1;
    if (!a.is_main && b.is_main) return 1;
    return (a.position ?? 0) - (b.position ?? 0);
  });
  return sorted.slice(0, 3).map((row, index) => ({
    slot: (index + 1) as ListingPhotoSlotIndex,
    url: row.url,
  }));
}

export function stripListingPhotoFeedbackSlugs(
  codes: Iterable<string>,
): Set<string> {
  const next = new Set<string>();
  for (const code of codes) {
    if (!isListingPhotoFeedbackSlug(code)) {
      next.add(code);
    }
  }
  return next;
}

export function photoSlotsFromReasonCodes(
  reasonCodes: Iterable<string>,
): ListingPhotoSlotIndex[] {
  const slots: ListingPhotoSlotIndex[] = [];
  for (const code of reasonCodes) {
    if (code === ALL_LISTING_PHOTOS_SLUG) {
      return [];
    }
    for (const [slot, slug] of Object.entries(LISTING_PHOTO_SLOT_SLUG) as [
      string,
      string,
    ][]) {
      if (slug === code) {
        slots.push(Number(slot) as ListingPhotoSlotIndex);
      }
    }
  }
  return slots.sort((a, b) => a - b);
}

export function hasAllPhotosFeedbackSlug(
  reasonCodes: Iterable<string>,
): boolean {
  return [...reasonCodes].includes(ALL_LISTING_PHOTOS_SLUG);
}

/** Toggle one slot; clears generic all-photos slug. Slot-specific slugs are mutually combinable. */
export function togglePhotoSlotInReasonCodes(
  codes: Set<string>,
  slot: ListingPhotoSlotIndex,
): Set<string> {
  const next = stripListingPhotoFeedbackSlugs(codes);
  const selectedSlots = new Set(photoSlotsFromReasonCodes(codes));
  if (selectedSlots.has(slot)) {
    selectedSlots.delete(slot);
  } else {
    selectedSlots.add(slot);
  }
  for (const s of [...selectedSlots].sort((a, b) => a - b)) {
    next.add(LISTING_PHOTO_SLOT_SLUG[s]);
  }
  return next;
}

/** Select or clear all-photos preset; mutually exclusive with slot-specific slugs. */
export function setAllPhotosFeedbackInReasonCodes(
  codes: Set<string>,
  enabled: boolean,
): Set<string> {
  const next = stripListingPhotoFeedbackSlugs(codes);
  if (enabled) {
    next.add(ALL_LISTING_PHOTOS_SLUG);
  }
  return next;
}

/** Short tips appended to WhatsApp / email when a photo preset is selected. */
export const LISTING_PHOTO_CHANNEL_TIPS =
  "Photo tips: use daylight or a bright room, wipe your phone lens, use a plain background, and show the full item (not only packaging).";

/**
 * Listing-issue rows for improvement presets (needs_user_action context).
 * Slot-specific slugs let admins cite main vs gallery photo 2 vs 3.
 */
export function getListingPhotoIssueDefinitions(): ListingPhotoIssueDefinition[] {
  return [
    {
      id: "image_quality",
      label: "All photos need improvement",
      description:
        "One or more photos are blurry, dark, or do not show the item clearly",
      messageText: [
        "* Photos — All three listing photos must be sharp, well lit, and show the actual item (not stock images or hidden in packaging).",
        "* Main photo — Clear front view in good light; this is your search cover.",
        "* Gallery photos 2 & 3 — Include a detail/label shot and another angle for size and condition.",
        "* Lighting & background — Daylight or a bright room; plain, uncluttered background.",
      ].join("\n"),
    },
    {
      id: "image_quality_main",
      label: "Main photo (photo 1 / search cover)",
      description:
        "The main cover image is blurry, poorly lit, or does not show the item clearly",
      messageText:
        "* Main photo (photo 1, search cover) — Replace with a sharp, well-lit front view of the whole item. Buyers see this first in search and on cards.",
    },
    {
      id: "image_quality_photo_2",
      label: "Gallery photo 2",
      description:
        "The second photo is blurry, dark, or missing important detail",
      messageText:
        "* Gallery photo 2 — Replace with a clear detail or label shot (brand, model, serial, or condition notes buyers should see).",
    },
    {
      id: "image_quality_photo_3",
      label: "Gallery photo 3",
      description:
        "The third photo is blurry, dark, or does not add useful context",
      messageText:
        "* Gallery photo 3 — Replace with another angle or context shot (side view, scale, or any defects). All three photos must stay sharp and well lit.",
    },
  ];
}

/** Seller-facing hint when status is needs_user_action and feedback includes photo presets. */
export function getPhotoResubmissionHint(
  reasonCodes?: string[] | null,
): string | null {
  if (!reasonCodes?.some(isListingPhotoFeedbackSlug)) {
    return null;
  }
  const hasSlotSpecific = reasonCodes.some(
    (c) => c !== "image_quality" && isListingPhotoFeedbackSlug(c),
  );
  if (hasSlotSpecific) {
    return "Replace the photo slot(s) called out below — tap Edit listing, swap the marked image(s), then save to resubmit.";
  }
  return "Improve all three listing photos as described below — sharp, well lit, and showing the actual item — then save to resubmit.";
}

export function appendPhotoTipsToFeedbackSummary(
  summary: string,
  reasonCodes?: string[] | null,
): string {
  if (!reasonCodes?.some(isListingPhotoFeedbackSlug)) {
    return summary;
  }
  const trimmed = summary.trim();
  if (trimmed.includes(LISTING_PHOTO_CHANNEL_TIPS)) {
    return trimmed;
  }
  return trimmed
    ? `${trimmed}\n\n${LISTING_PHOTO_CHANNEL_TIPS}`
    : LISTING_PHOTO_CHANNEL_TIPS;
}
